import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plane, ArrowRight, Car, Hotel, Landmark, Lightbulb } from "lucide-react";
import api from "../lib/api";

/* IndianAirports.com — route page ({ORIG}-{DEST}), feeding into the TravelerPulse planner. */
export default function IndiaRoutePage() {
  const { pair } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/india/airports/routes/${pair}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.origin.city} → ${r.data.destination.city} — IndianAirports.com`;
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [pair]);

  if (err) return <div className="max-w-4xl mx-auto px-5 py-16 text-center text-sm text-zinc-500" data-testid="india-route-notfound">Route not found. <Link to="/india/airports/directory" className="text-blue-700 font-700 hover:underline">Browse airports</Link></div>;
  if (!data) return <div className="max-w-4xl mx-auto px-5 py-12 text-sm text-zinc-500">Loading route…</div>;
  const { origin: o, destination: d } = data;

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="india-route-page">
      <Link to={`/india/airports/${o.slug}`} className="text-xs font-700 text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid="india-route-back"><ArrowLeft className="w-3.5 h-3.5" /> {o.iata} airport guide</Link>
      <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="india-route-title">
        {o.city} <ArrowRight className="inline w-7 h-7 text-orange-500 -mt-1" /> {d.city}
      </h1>
      <p className="mt-1 text-sm text-zinc-500 font-mono">{o.iata} → {d.iata}</p>

      {/* Airport cards */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {[["From", o], ["To", d]].map(([lbl, a]) => (
          <Link key={a.iata} to={`/india/airports/${a.slug}`} className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-blue-400" data-testid={`india-route-airport-${a.iata}`}>
            <div className="relative h-28 overflow-hidden">
              <img src={a.image_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <span className="absolute top-2 left-2 bg-white/90 text-[10px] font-800 uppercase rounded px-1.5 py-0.5">{lbl}</span>
              <span className="absolute top-2 right-2 bg-blue-700/90 text-white font-mono font-800 text-sm rounded px-2 py-0.5">{a.iata}</span>
            </div>
            <div className="p-3.5">
              <div className="text-sm font-800">{a.name}</div>
              <div className="text-xs text-zinc-500">{a.city}, {a.state}{a.distance_to_city ? ` · ${a.distance_to_city} to city` : ""}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Airlines */}
      <section className="mt-8" data-testid="india-route-airlines">
        <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><Plane className="w-5 h-5 text-blue-700" /> Airlines serving both airports</h2>
        {data.common_airlines.length === 0 ? (
          <p className="mt-2 text-sm text-zinc-500">No single airline appears on both airport profiles — this route may need a connection.</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {data.common_airlines.map((al) => (
              <Link key={al.slug} to={`/india/airlines/${al.slug}`} className="text-xs font-700 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-full px-3 py-1.5 hover:border-emerald-400" data-testid={`india-route-airline-${al.slug}`}>{al.name}</Link>
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-zinc-400">Derived from airport profiles — not a confirmed nonstop schedule. Check timetables with the airline.</p>
      </section>

      {/* At the destination */}
      <section className="mt-8" data-testid="india-route-destination">
        <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><Car className="w-5 h-5 text-blue-700" /> When you land at {d.iata}</h2>
        <div className="mt-3 space-y-2">
          {d.ground_transport.map((g) => (
            <div key={g.mode} className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm">
              <span className="font-700 w-24 shrink-0">{g.mode}</span>
              <span className="text-zinc-600 flex-1 min-w-[180px]">{g.detail}</span>
              {g.fare && !/^(n\/a|varies)$/i.test(g.fare.trim()) && <span className="text-xs font-700 text-emerald-700">{g.fare}</span>}
            </div>
          ))}
        </div>
        {d.hotels.length > 0 && (
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {d.hotels.map((h) => (
              <div key={h.name} className="bg-white border border-zinc-200 rounded-xl px-3.5 py-2.5 text-sm">
                <span className="font-700 inline-flex items-center gap-1.5"><Hotel className="w-3.5 h-3.5 text-blue-600" /> {h.name}</span>
                <span className="block text-xs text-zinc-500 mt-0.5">{h.distanceKm != null ? `${h.distanceKm} km` : ""}{h.priceRange ? ` · ${h.priceRange}` : ""}</span>
              </div>
            ))}
          </div>
        )}
        {d.tourism.length > 0 && (
          <p className="mt-3 text-sm text-zinc-600 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <Landmark className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
            <span><b>Beyond {d.city}:</b> {d.tourism.map((t) => t.name).join(", ")}.</span>
          </p>
        )}
        {o.tips.length > 0 && (
          <p className="mt-3 text-sm text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span><b>Before departure from {o.iata}:</b> {o.tips[0]}</span>
          </p>
        )}
      </section>

      {/* TravelerPulse feeder */}
      <div className="mt-8 bg-blue-700 text-white rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid="india-route-plan-cta">
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading font-800 text-lg">Plan this trip end to end</div>
          <p className="text-xs text-blue-100 mt-0.5">Flights, stays, transport and a day-by-day plan for {d.city} — organized in TravelerPulse.</p>
        </div>
        <Link to="/plan-ai" data-testid="india-route-plan-btn" className="bg-white text-blue-800 text-sm font-800 rounded-full px-5 py-2.5 hover:bg-blue-50">Plan This Trip in TravelerPulse</Link>
      </div>
    </div>
  );
}
